import React, {useEffect} from 'react'
import {noop} from 'ts-essentials'
import {ffLazyLoading, hasFeatureFlag} from 'yougile-util'
import {Hub, isHub} from 'yougile-model'
import {Refresh} from '@client/refresh'
import {Sync} from '@client/sync'
import {Backend} from '@common/backend'
import {Checkbox} from '@components/common/base/checkbox'
import {Tooltip} from '@components/common/tooltip/tooltip'

// TODO: Remove after initial testing
export const LazyMonitor: React.FC = () => {
  const [isMonitorEnabled, setIsMonitorEnabled] = React.useState<boolean>(
    () => {
      return localStorage.getItem('isMonitorEnabled') === 'true'
    }
  )
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (
        (event.metaKey || event.ctrlKey) &&
        event.shiftKey &&
        event.key.toLowerCase() === 'l'
      ) {
        setIsMonitorEnabled(enabled => {
          localStorage.setItem('isMonitorEnabled', String(!enabled))
          return !enabled
        })
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  })
  const [memory, setMemory] = React.useState<number[]>([])
  const [totalItems, setTotalItems] = React.useState<number>(0)
  const [totalLazyItems, setTotalLazyItems] = React.useState<number>(0)
  const [totalChats, setTotalChats] = React.useState<number>(0)
  const [totalLazyChats, setTotalLazyChats] = React.useState<number>(0)
  const [totalMessagesCount, setTotalMessagesCount] = React.useState<number>(0)
  const [messagesLoadedCount, setMessagesLoadedCount] =
    React.useState<number>(0)
  const box = Sync.getCurrentSandbox()
  useEffect(() => {
    if (!isMonitorEnabled) {
      return noop
    }
    const entities = box?.entities || {}
    const entitiesValues = Object.values(entities)
    const interval = setInterval(() => {
      const totalChats = entitiesValues.filter(
        entity => isHub(entity) && !entity.isLazy
      ).length
      const totalLazyChats = entitiesValues.filter(
        (entity: Hub) => entity.chat?.computeIsLazyChat()
      ).length
      const totalMessagesCount = entitiesValues.reduce(
        (acc: number, entity: Hub) => acc + (entity.chat?.length || 0),
        0
      )
      const messagesLoadedCount = entitiesValues.reduce(
        (acc: number, entity: Hub) => acc + (entity.chat?.messages?.size || 0),
        0
      )
      setTotalChats(totalChats)
      setTotalLazyChats(totalLazyChats)
      setTotalMessagesCount(totalMessagesCount)
      setMessagesLoadedCount(messagesLoadedCount)
    }, 5000)
    return () => clearInterval(interval)
  }, [box?.revision, isMonitorEnabled])
  useEffect(() => {
    if (!isMonitorEnabled) {
      return noop
    }
    const interval = setInterval(() => {
      try {
        const currentMemory =
          (performance as any).memory.usedJSHeapSize / 1024 / 1024 / 1024
        setMemory(memory => [...memory, currentMemory].slice(-30))
      } catch {
        // ignore
      }
    }, 2000)
    return () => clearInterval(interval)
  }, [isMonitorEnabled])

  useEffect(() => {
    if (!isMonitorEnabled) {
      return noop
    }

    const entities = box?.entities || {}
    const items = Object.keys(entities).filter(key =>
      isHub(entities[key])
    ).length
    const values = Object.values(entities).filter(entity => isHub(entity))

    const interval = setInterval(() => {
      const lazyItems = values.filter(entity => entity.isLazy).length
      setTotalItems(items)
      setTotalLazyItems(lazyItems)
    }, 5000)
    return () => clearInterval(interval)
  }, [box?.revision, isMonitorEnabled])

  if (!isMonitorEnabled) {
    return null
  }

  const lastMemory = memory[memory.length - 1]

  const maxMemory = Math.max(...memory) || 0

  const lazyItemsPercent = (totalLazyItems / totalItems) * 100

  return (
    <div className="fixed origin-bottom-left scale-75 flex flex-col items-start bottom-0 left-0 z-2000 p-4 bg-grey-d80 text-grey-l100 opacity-75 rounded-tr-lg">
      {!!totalItems && (
        <div>
          <Tooltip
            zIndex={10000}
            content="Кол-во частично загруженных хабов из всех существующих в компании"
          >
            <span>?</span>
          </Tooltip>{' '}
          Ленивые хабы: {totalLazyItems} из {totalItems} (
          {lazyItemsPercent.toFixed(2)}
          %)
        </div>
      )}
      {!!lazyItemsPercent && (
        <div className="h-2 bg-grey-d80 w-full">
          <div
            className="h-2 bg-grey-l100"
            style={{width: `${lazyItemsPercent}%`}}
          />
        </div>
      )}
      {!!totalChats && (
        <div>
          <Tooltip
            zIndex={10000}
            content="Кол-во хабов, которые были загружены полностью, но без полного чата"
          >
            <span>?</span>
          </Tooltip>{' '}
          Ленивые чаты: {totalLazyChats} из {totalChats}
        </div>
      )}
      {!!totalMessagesCount && (
        <div>
          <Tooltip
            zIndex={10000}
            content={`Из загруженных полностью хабов с чатами (${totalChats}шт) загружено сообщений (${Math.round(
              (messagesLoadedCount / totalMessagesCount) * 100
            )}%)`}
          >
            <span>?</span>
          </Tooltip>{' '}
          Загружено сообщений: {messagesLoadedCount} из {totalMessagesCount} (
          {Math.round((messagesLoadedCount / totalMessagesCount) * 100)}% )
        </div>
      )}
      <div>Кеш запросов: {Backend.ongoingPostJSONs.size}</div>
      {lastMemory && <div>Память: {lastMemory.toFixed(2)} GB</div>}
      <div className="flex flex-row justify-end items-end h-20 w-60 gap-1">
        {memory.map((value, index) => {
          const percent = (value / maxMemory) * 100
          return (
            <div
              key={index}
              className="w-1 bg-grey-l100"
              style={{height: `${percent}%`}}
            />
          )
        })}
      </div>
      <div
        className="flex flex-row w-full gap-2 items-center"
        onClick={() => {
          if (hasFeatureFlag(ffLazyLoading)) {
            const features = window.localStorage.getItem('features') || ''
            window.localStorage.setItem(
              'features',
              features
                .split(';')
                .filter(f => f !== ffLazyLoading)
                .join(';')
            )
          } else {
            const features = window.localStorage.getItem('features') || ''
            window.localStorage.setItem(
              'features',
              features + ';' + ffLazyLoading
            )
          }
          void Refresh.clearCredentials(true)
        }}
      >
        <Checkbox
          value={hasFeatureFlag(ffLazyLoading)}
          text=""
          className="text-grey-l100"
        />
        <span className="text-grey-l100">Ленивый режим</span>
      </div>
    </div>
  )
}
